/**
 * 金额口径单一事实源（实收允许大于订单金额，决策 2026-09-10 选①）。
 *
 * 后端 `orders.service.getOrderStats`、管理端看板、打印文档与核算月报用的是同一套
 * 「逐单夹零」算法；四端展示层必须跟着夹零，否则同一张多收的单会在某处显示负待收、
 * 在另一处显示 0，用户就会以为账算错了。
 */

/** 待收余款：逐单夹零。多收的那张单待收记 0，不产生负的待收 */
export function pendingBalanceOf(orderAmount: number, paidAmount: number): number {
  return Math.max(0, orderAmount - paidAmount);
}

/** 多收金额：实收超过订单金额的部分。0 = 没有多收，展示层据此决定是否显示「多收」 */
export function overPaymentOf(orderAmount: number, paidAmount: number): number {
  return Math.max(0, paidAmount - orderAmount);
}

/**
 * 付款状态判定：`orders.payment_status` 这一 PG 枚举的三个原值（未付款/部分付款/已结清），web 端唯一实现。
 *
 * 入参收 string 也收 number：表单里是输入框的原始串，详情与打印件里是后端回读的数值，
 * 两者对 `Number()` 等价（'' / null → 0），合成一份就不用再养两个同式副本。
 * 判定式与后端 `computePaymentStatus` 同式；「实收 >= 应收即结清」「0 元实收算未付款」都是既定口径。
 *
 * 这里**刻意不做** 0 元单的「无金额」映射：表单拿返回值比较 `!== '未付款'` 来决定收款入口，
 * 掺进展示文案就把判定也一起改了。要那句文案的调用方自己套 `paymentStatusLabel`。
 */
export function paymentStatusOf(
  orderAmount: number | string | null | undefined,
  paidAmount: number | string | null | undefined,
): string {
  const total = Number(orderAmount ?? 0);
  const paid = Number(paidAmount ?? 0);
  if (paid <= 0) return '未付款';
  if (total > 0 && paid >= total) return '已结清';
  return '部分付款';
}

/**
 * 0 元单的唯一判据：应收**已知且**为 0（「先占档期后定价」的占位单）。
 *
 * null/undefined 表示这条数据没带金额，按 0 处理会把整列订单误标成「无金额」，
 * 与 Flutter `isZeroAmountOrder` 保持同一套严格语义。
 */
export function isZeroAmountOrder(orderAmount: number | string | null | undefined): boolean {
  return orderAmount != null && orderAmount !== '' && Number(orderAmount) <= 0;
}

/** 0 元单在展示层不叫「未付款」，叫「无金额」 */
export const NO_AMOUNT_LABEL = '无金额';

/**
 * 这张单有没有收款义务：应收为 0 **且** 一分钱都没收到，才算「无」。
 *
 * 界面据此整块不显示付款徽章（裁定 2026-09-30）：0 元占位单既不是欠款（红色「未付款」误导），
 * 也不算收齐（看板那一版把它显示成「已结清」更错）。
 * 收了钱的 0 元单不在此列——实收是事实，徽章必须留着告诉经手人。
 */
export function hasNoPaymentObligation(
  orderAmount: number | string | null | undefined,
  paidAmount: number | string | null | undefined,
): boolean {
  return isZeroAmountOrder(orderAmount) && Number(paidAmount ?? 0) <= 0;
}

/**
 * 这张单有没有留下过收款痕迹：实收、方式、日期、凭证任一有值即算。
 *
 * 展示层据此决定「费用与收款」给不给「已付金额 / 支付方式」两行（裁定 2026-10-02）：
 * 一分没收的单摆出 `¥0.00` 和 `-` 不是信息，是噪声，还会让人以为钱记丢了。
 * 判据刻意比 `paymentStatusOf` 宽——历史单可能金额为零却留着方式和日期（#152 那 20 张），
 * 那种单有痕迹，必须照原样显示，不能因为「状态=未付款」就把痕迹抹掉。
 */
export function hasPaymentRecord(
  paidAmount: number | string | null | undefined,
  paymentMethod?: string | null,
  paymentDate?: string | null,
  paymentVoucherUrl?: string | null,
): boolean {
  return Number(paidAmount ?? 0) > 0
    || !!paymentMethod?.trim()
    || !!paymentDate?.trim()
    || !!paymentVoucherUrl?.trim();
}

/**
 * 展示层付款状态：0 元单映射成「无金额」。
 *
 * 后端 payment_status 枚举里没有「无金额」，库里 0 元单落的就是「未付款」——
 * 这句话只出现在界面上，不参与任何判定与比较，所以四端各自比较 `=== '未付款'` 的逻辑不受影响。
 */
export function paymentStatusLabel(status: string, orderAmount: number | string | null | undefined): string {
  return isZeroAmountOrder(orderAmount) ? NO_AMOUNT_LABEL : status;
}

/**
 * 把算出来的钱夹回两位小数（`Decimal(10,2)` 能原样存下的那种）。
 *
 * 浮点运算的尾数是必然而不是意外：数量 × 单价（1.15 × 3 = 3.4499999999999997）、
 * 多张单的未付额求和（三个 22.22 相加 = 66.67000000000002）。界面按两位显示，
 * 上送的却是那条长尾数，而服务端的 `@MaxTwoDecimals` 闸门正是来挡「存的不是用户看到的」——
 * 不先夹一下，合法订单会吃自己的 400。显示什么就存什么，三端同这一条。
 * 算式与后端 `isOverTwoDecimals` 刻意一致（`Math.round(v * 100) / 100`），归整结果才保证过校验。
 */
export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/** 能吃下表单行（输入框里的字符串）与详情行（后端回读的数值）两种形状 */
export interface ItemAmountRow {
  quantity?: number | string | null;
  unitPrice?: number | string | null;
}

/**
 * 明细每日小计 = Σ roundMoney(数量 × 每日单价)，逐行夹两位、求和后对合计再夹一次。
 *
 * web 端唯一实现：表单、详情抽屉与三张打印模板都必须走这里。算式与后端
 * `orders/order-amount-guard.orderItemsDailySubtotal` 一字不差——服务端拿它复核
 * `order_amount`，任何一端少夹一次，界面上就会出现一个「后端不认的数」。
 * 逐行归整后仍可能留下浮点尾数（0.1×3 + 0.2×3 = 0.8999999999999999），所以末尾还要再夹。
 */
export function itemsDailySubtotal(rows: ItemAmountRow[]): number {
  const sum = rows.reduce(
    (acc, row) => acc + roundMoney(Number(row.quantity ?? 0) * Number(row.unitPrice ?? 0)),
    0,
  );
  return roundMoney(sum);
}
